/**
 * Página /#agente-atendimento — Monitoramento → Agente Atendimento IA (2026-09-28).
 *
 * O agente (claude-hq) analisa uma vez por dia (06:30) os atendimentos encerrados na véspera no Opa Suite: SLA,
 * motivo, problema técnico e risco de cancelamento, mais um resumo executivo. Grava em tabelas atd_* deste banco;
 * aqui é só leitura. Cliente aparece só por id do IXC + nome (sem CPF/telefone/e-mail).
 *
 *   #agente-atendimento                   dias analisados
 *   #agente-atendimento/<AAAA-MM-DD>      relatório do dia + atendimentos
 *   #agente-atendimento/cliente/<id>      histórico do cliente
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';

type Dia = {
  data: string; atendimentos: number; analisados: number; abandonados: number; sla1: number | null; sla2: number | null;
  clientesRisco: number; tecnicos: number; custoUsd: number; custoClassificacaoUsd: number; custoResumoUsd: number;
  custoFalhasUsd: number; geradoEm: string | null; temResumo: boolean;
};
type Grupo = { rotulo: string; qtd: number; sla1: number | null; sla2: number | null; abandonados?: number };
type Risco = { idClienteIxc: number; nome: string | null; qtd: number; irritado: boolean; falaCancelar: boolean; risco: boolean; anteriores30d: number };
type Relatorio = {
  data: string;
  totais: { atendimentos: number; analisados: number; pendentes: number; abandonados: number; tecnicos: number; encerradosBot: number; sla1: number | null; sla2: number | null; custoUsd: number; custoMedioUsd: number | null; modelos: string[] };
  relatorio: { geradoEm: string | null; resumoGestao: string | null; custoTotalUsd: number | null; custoResumoUsd: number | null; custoFalhasUsd: number | null } | null;
  departamentos: Grupo[]; atendentes: Grupo[];
  motivos: Array<{ motivo: string; qtd: number }>;
  clusters: Array<{ olt: string; qtd: number; protocolos: string[] }>;
  risco: Risco[];
  exemplos: Array<{ protocolo: string; departamento: string | null; motivo: string | null; resumo: string | null }>;
};
type Atendimento = {
  id: string; protocolo: string | null; data: string; abertoEm: string; encerradoEm: string | null;
  idClienteIxc: number | null; nomeCliente: string | null; departamento: string | null; atendente: string | null;
  tipoAtendente: string | null; canal: string | null; olt: string | null; sla1: number | null; sla2: number | null;
  abandonado: boolean; qtdMensagens: number; motivo: string | null; problemaTecnico: boolean | null;
  irritado: boolean | null; falaCancelar: boolean | null; risco: boolean | null; resumo: string | null; analisadoEm: string | null;
  modelo: string | null; custoUsd: number | null;
};

const go = (h: string) => { window.location.hash = '#agente-atendimento' + (h ? '/' + h : ''); };
const dt = (s: string | null) => (s ? new Date(s).toLocaleString('pt-BR', { hour12: false }) : '—');
const hora = (s: string | null) => (s ? new Date(s).toLocaleTimeString('pt-BR', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '—');
const diaBr = (d: string) => { const [a, m, dd] = d.split('-'); return `${dd}/${m}/${a}`; };
const semana = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('pt-BR', { weekday: 'short' });
const usd = (n: number | null | undefined) => (n == null ? '—' : `US$ ${n.toFixed(2)}`);
// custo de um atendimento é fração de centavo: 4 casas
const usd4 = (n: number | null | undefined) => (n == null ? '—' : `US$ ${n.toFixed(4)}`);
const pct = (a: number, b: number) => (b ? `${Math.round((100 * a) / b)}%` : '—');

function dur(s: number | null | undefined): string {
  if (s == null) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}m`;
}
// faixas de SLA (mesmas do relatório enviado à gestão): até 30 min ok; 1ª resposta até 2h / resolução até 6h atenção
function corSla(s: number | null | undefined, tipo: 'primeira' | 'resolucao'): string {
  if (s == null) return 'text-slate-500';
  if (s <= 1800) return 'text-emerald-400';
  return s <= (tipo === 'primeira' ? 7200 : 21600) ? 'text-amber-400' : 'text-rose-400';
}
function Sla({ s, tipo }: { s: number | null | undefined; tipo: 'primeira' | 'resolucao' }) {
  return <span className={`font-mono ${corSla(s, tipo)}`}>{dur(s)}</span>;
}

export function AtendimentoAgentePage({ subRoute }: { subRoute: string | null }) {
  const cli = subRoute?.match(/^cliente\/(\d+)$/);
  if (cli) return <Cliente id={Number(cli[1])} />;
  if (subRoute && /^\d{4}-\d{2}-\d{2}$/.test(subRoute)) return <RelatorioDia data={subRoute} />;
  return <Dias />;
}

function Card({ titulo, extra, children }: { titulo: string; extra?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-300">{titulo}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}

function Kpi({ l, v, cor, sub }: { l: string; v: React.ReactNode; cor?: string; sub?: string }) {
  return (
    <div className="rounded border border-slate-800 bg-slate-900/60 p-3">
      <div className="text-xs text-slate-400">{l}</div>
      <div className={`text-lg font-semibold ${cor ?? ''}`}>{v}</div>
      {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
  );
}

const Flag = ({ on, txt, cor }: { on: boolean | null | undefined; txt: string; cor: string }) =>
  on ? <span className={`px-1.5 py-0.5 rounded text-[10px] ring-1 ${cor}`}>{txt}</span> : null;

// ---------------------------------------------------------------- lista de dias

function Dias() {
  const q = useQuery<{ dias: Dia[] }>({
    queryKey: ['atd-agente', 'dias'],
    queryFn: () => api.get('/api/atendimento-agente/dias'),
    refetchInterval: 300_000
  });
  const dias = q.data?.dias ?? [];
  const maxAt = Math.max(1, ...dias.map((d) => d.atendimentos));
  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Agente Atendimento IA <span className="text-sm text-slate-500">— análise diária dos atendimentos do Opa Suite</span></h2>
        <span className="text-xs text-slate-500">roda todo dia às 06:30 com os atendimentos encerrados na véspera</span>
      </div>
      <div className="rounded border border-slate-800 bg-slate-900/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-800/60 text-slate-400">
            <tr>
              <th className="text-left px-3 py-2">Dia</th>
              <th className="text-left px-3 py-2 w-56">Atendimentos analisados</th>
              <th className="text-right px-3 py-2">Abandono</th>
              <th className="text-right px-3 py-2">1ª resposta humana</th>
              <th className="text-right px-3 py-2">Resolução</th>
              <th className="text-right px-3 py-2">Problema técnico</th>
              <th className="text-right px-3 py-2">Clientes em risco</th>
              <th className="text-right px-3 py-2">Custo</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && <tr><td colSpan={8} className="p-4 text-center text-slate-500">carregando…</td></tr>}
            {q.isError && <tr><td colSpan={8} className="p-4 text-center text-rose-400">erro ao carregar</td></tr>}
            {dias.map((d) => (
              <tr key={d.data} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => go(d.data)}>
                <td className="px-3 py-1.5 whitespace-nowrap">
                  <span className="font-mono text-slate-200">{diaBr(d.data)}</span>
                  <span className="ml-2 text-xs text-slate-500">{semana(d.data)}</span>
                </td>
                <td className="px-3 py-1.5">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded bg-slate-800 overflow-hidden">
                      <div className="h-full bg-cyan-600" style={{ width: `${(100 * d.atendimentos) / maxAt}%` }} />
                    </div>
                    <span className="font-mono text-xs text-slate-300 w-16 text-right">{d.analisados}/{d.atendimentos}</span>
                  </div>
                </td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-300">{pct(d.abandonados, d.atendimentos)}</td>
                <td className="px-3 py-1.5 text-right text-xs"><Sla s={d.sla1} tipo="primeira" /></td>
                <td className="px-3 py-1.5 text-right text-xs"><Sla s={d.sla2} tipo="resolucao" /></td>
                <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-300">{d.tecnicos}</td>
                <td className={`px-3 py-1.5 text-right font-mono text-xs ${d.clientesRisco ? 'text-rose-400' : 'text-slate-500'}`}>{d.clientesRisco}</td>
                <td className="px-3 py-1.5 text-right text-xs text-slate-400 whitespace-nowrap"
                  title={`classificação ${usd4(d.custoClassificacaoUsd)} · resumo ${usd4(d.custoResumoUsd)}${d.custoFalhasUsd ? ` · lotes com falha ${usd4(d.custoFalhasUsd)}` : ''}`}>
                  {usd(d.custoUsd)}{d.analisados ? <div className="text-[10px] text-slate-500">{usd4(d.custoClassificacaoUsd / d.analisados)}/atend.</div> : null}
                </td>
              </tr>
            ))}
            {q.data && dias.length === 0 && <tr><td colSpan={8} className="p-4 text-center text-slate-500">nenhum dia analisado ainda</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500">
        SLA: até 30 min <span className="text-emerald-400">ok</span> · até 2h (1ª resposta) ou 6h (resolução) <span className="text-amber-400">atenção</span> · acima <span className="text-rose-400">crítico</span>.
        Cliente em risco = falou em cancelar ou o modelo marcou risco.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------- relatório do dia

function RelatorioDia({ data }: { data: string }) {
  const q = useQuery<Relatorio>({
    queryKey: ['atd-agente', 'dia', data],
    queryFn: () => api.get(`/api/atendimento-agente/dias/${data}`)
  });
  const [filtro, setFiltro] = useState<{ departamento: string; motivo: string; sinal: string; q: string }>({ departamento: '', motivo: '', sinal: 'todos', q: '' });
  const irPara = (f: Partial<typeof filtro>) => {
    setFiltro((x) => ({ ...x, departamento: '', motivo: '', sinal: 'todos', q: '', ...f }));
    document.getElementById('atd-lista')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (q.isLoading) return <div className="p-6 text-slate-400">carregando…</div>;
  if (q.isError || !q.data) return <div className="p-6 text-rose-400">dia sem análise</div>;
  const { totais: t, relatorio, departamentos, atendentes, motivos, clusters, risco, exemplos } = q.data;
  const maxMot = Math.max(1, ...motivos.map((m) => m.qtd));

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => go('')}>← dias</button>
          <h2 className="text-xl font-semibold">Atendimento de {diaBr(data)} <span className="text-sm text-slate-500">{semana(data)}</span></h2>
        </div>
        <div className="text-right text-xs text-slate-400 space-y-0.5">
          <div>relatório gerado {dt(relatorio?.geradoEm ?? null)}</div>
          <div>custo total {usd(t.custoUsd + (relatorio?.custoResumoUsd ?? 0) + (relatorio?.custoFalhasUsd ?? 0))}</div>
        </div>
      </div>

      {relatorio?.resumoGestao && (
        <Card titulo="Resumo para a gestão">
          <p className="text-sm text-slate-200 leading-relaxed max-w-4xl">{relatorio.resumoGestao}</p>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi l="Atendimentos analisados" v={`${t.analisados}/${t.atendimentos}`} sub={t.pendentes ? `${t.pendentes} pendente(s)` : undefined} />
        <Kpi l="Encerrados pelo bot" v={pct(t.encerradosBot, t.atendimentos)} sub={`${t.encerradosBot} atendimento(s)`} />
        <Kpi l="Abandonados" v={t.abandonados} sub={pct(t.abandonados, t.atendimentos)} cor={t.abandonados ? 'text-amber-400' : undefined} />
        <Kpi l="1ª resposta humana (méd.)" v={dur(t.sla1)} cor={corSla(t.sla1, 'primeira')} />
        <Kpi l="Resolução (méd.)" v={dur(t.sla2)} cor={corSla(t.sla2, 'resolucao')} />
        <Kpi l="Clientes em risco" v={risco.filter((r) => r.risco || r.falaCancelar).length} cor="text-rose-400"
          sub={`${risco.filter((r) => r.falaCancelar).length} falaram em cancelar`} />
      </div>

      <Card titulo="Custo da análise">
        <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
          <div><div className="text-xs text-slate-400">Classificação ({t.modelos.join(', ') || '—'})</div><div className="font-mono">{usd4(t.custoUsd)}</div>
            <div className="text-[11px] text-slate-500">{usd4(t.custoMedioUsd)} por atendimento · {t.analisados} analisado(s)</div></div>
          <div><div className="text-xs text-slate-400">Resumo para a gestão</div><div className="font-mono">{usd4(relatorio?.custoResumoUsd)}</div>
            <div className="text-[11px] text-slate-500">uma chamada por relatório gerado</div></div>
          <div><div className="text-xs text-slate-400">Lotes que falharam</div><div className={`font-mono ${relatorio?.custoFalhasUsd ? 'text-amber-400' : ''}`}>{usd4(relatorio?.custoFalhasUsd ?? 0)}</div>
            <div className="text-[11px] text-slate-500">gasto sem classificar (reprocessado no run seguinte)</div></div>
          <div><div className="text-xs text-slate-400">Total do dia</div><div className="font-mono font-semibold">{usd4(t.custoUsd + (relatorio?.custoResumoUsd ?? 0) + (relatorio?.custoFalhasUsd ?? 0))}</div></div>
        </div>
        <p className="text-[11px] text-slate-500">O custo de cada atendimento é o custo do lote (até 15 atendimentos numa chamada) dividido pelos que o lote classificou.</p>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card titulo="SLA por departamento">
          <TabelaGrupo linhas={departamentos} comAbandono onClick={(r) => irPara({ departamento: r })} />
        </Card>
        <Card titulo="SLA por atendente (humano)">
          <TabelaGrupo linhas={atendentes} />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card titulo="Principais motivos">
          <div className="space-y-1.5">
            {motivos.map((m) => (
              <button key={m.motivo} className="w-full grid grid-cols-[minmax(0,11rem)_1fr_2rem] gap-2 items-center text-xs text-left hover:text-white"
                onClick={() => irPara({ motivo: m.motivo })} title={m.motivo}>
                <span className="truncate text-slate-300">{m.motivo}</span>
                <span className="h-2 rounded bg-slate-800 overflow-hidden"><span className="block h-full bg-cyan-600" style={{ width: `${(100 * m.qtd) / maxMot}%` }} /></span>
                <span className="text-right font-mono text-slate-400">{m.qtd}</span>
              </button>
            ))}
            {motivos.length === 0 && <div className="text-xs text-slate-500">sem classificação</div>}
          </div>
        </Card>
        <Card titulo="Clusters técnicos (sinal para o NOC)">
          {clusters.length === 0 && <div className="text-xs text-slate-500">nenhum: não houve 2 ou mais problemas técnicos na mesma OLT/região</div>}
          {clusters.map((c) => (
            <button key={c.olt} className="w-full text-left text-xs border-t border-slate-800 pt-1 hover:text-white" onClick={() => irPara({ sinal: 'tecnico', q: c.olt })}>
              <span className="text-amber-400 font-semibold">{c.qtd}×</span> <span className="text-slate-200">{c.olt}</span>
              <div className="text-slate-500 font-mono truncate">{c.protocolos.join(', ')}</div>
            </button>
          ))}
        </Card>
        <Card titulo="Exemplos">
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {exemplos.map((e) => (
              <div key={e.protocolo} className="text-xs border-t border-slate-800 pt-1">
                <div><span className="font-mono text-cyan-400">{e.protocolo}</span> <span className="text-slate-500">{e.departamento} · {e.motivo}</span></div>
                <div className="text-slate-300">{e.resumo}</div>
              </div>
            ))}
            {exemplos.length === 0 && <div className="text-xs text-slate-500">nenhum</div>}
          </div>
        </Card>
      </div>

      <Card titulo={`Clientes em risco / reincidentes (${risco.length})`}
        extra={<span className="text-[11px] text-slate-500">id do IXC + nome · abra o cadastro no IXC pelo id</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-slate-400">
              <tr>
                <th className="text-right px-2 py-1">ID IXC</th><th className="text-left px-2 py-1">Nome</th>
                <th className="text-right px-2 py-1">No dia</th><th className="text-right px-2 py-1">30 dias antes</th>
                <th className="text-left px-2 py-1">Sinais</th>
              </tr>
            </thead>
            <tbody>
              {risco.map((r) => (
                <tr key={r.idClienteIxc} className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer" onClick={() => go(`cliente/${r.idClienteIxc}`)}>
                  <td className="px-2 py-1 text-right font-mono text-slate-400">{r.idClienteIxc}</td>
                  <td className="px-2 py-1 text-slate-200">{r.nome ?? '—'}</td>
                  <td className={`px-2 py-1 text-right font-mono ${r.qtd > 1 ? 'text-amber-400' : 'text-slate-400'}`}>{r.qtd}</td>
                  <td className={`px-2 py-1 text-right font-mono ${r.anteriores30d ? 'text-amber-400' : 'text-slate-500'}`}>{r.anteriores30d}</td>
                  <td className="px-2 py-1 space-x-1">
                    <Flag on={r.falaCancelar} txt="falou em cancelar" cor="ring-rose-500 text-rose-300" />
                    <Flag on={r.risco} txt="risco" cor="ring-rose-700 text-rose-300" />
                    <Flag on={r.irritado} txt="irritado" cor="ring-amber-600 text-amber-300" />
                    <Flag on={r.qtd > 1} txt="reincidente" cor="ring-slate-500 text-slate-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div id="atd-lista">
        <ListaAtendimentos data={data} filtro={filtro} setFiltro={setFiltro} />
      </div>
    </div>
  );
}

function TabelaGrupo({ linhas, comAbandono, onClick }: { linhas: Grupo[]; comAbandono?: boolean; onClick?: (rotulo: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-slate-400">
          <tr>
            <th className="text-left px-2 py-1">Nome</th><th className="text-right px-2 py-1">Atend.</th>
            <th className="text-right px-2 py-1">1ª resposta</th><th className="text-right px-2 py-1">Resolução</th>
            {comAbandono && <th className="text-right px-2 py-1">Abandonos</th>}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.rotulo} className={`border-t border-slate-800 ${onClick ? 'hover:bg-slate-800/40 cursor-pointer' : ''}`} onClick={() => onClick?.(l.rotulo)}>
              <td className="px-2 py-1 text-slate-200">{l.rotulo}</td>
              <td className="px-2 py-1 text-right font-mono text-slate-300">{l.qtd}</td>
              <td className="px-2 py-1 text-right"><Sla s={l.sla1} tipo="primeira" /></td>
              <td className="px-2 py-1 text-right"><Sla s={l.sla2} tipo="resolucao" /></td>
              {comAbandono && <td className={`px-2 py-1 text-right font-mono ${l.abandonados ? 'text-amber-400' : 'text-slate-500'}`}>{l.abandonados}</td>}
            </tr>
          ))}
          {linhas.length === 0 && <tr><td colSpan={5} className="px-2 py-2 text-slate-500">nenhum</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------- lista de atendimentos do dia

type Filtro = { departamento: string; motivo: string; sinal: string; q: string };

function ListaAtendimentos({ data, filtro, setFiltro }: { data: string; filtro: Filtro; setFiltro: React.Dispatch<React.SetStateAction<Filtro>> }) {
  const [texto, setTexto] = useState(filtro.q);
  useEffect(() => { setTexto(filtro.q); }, [filtro.q]);
  useEffect(() => {
    const t = setTimeout(() => setFiltro((f) => (f.q === texto.trim() ? f : { ...f, q: texto.trim() })), 400);
    return () => clearTimeout(t);
  }, [texto, setFiltro]);
  const params = new URLSearchParams({ data, sinal: filtro.sinal });
  if (filtro.departamento) params.set('departamento', filtro.departamento);
  if (filtro.motivo) params.set('motivo', filtro.motivo);
  if (filtro.q) params.set('q', filtro.q);
  const q = useQuery<{ atendimentos: Atendimento[]; departamentos: string[]; motivos: string[] }>({
    queryKey: ['atd-agente', 'lista', params.toString()],
    queryFn: () => api.get(`/api/atendimento-agente/atendimentos?${params}`),
    placeholderData: (prev) => prev
  });
  const set = <K extends keyof Filtro>(k: K, v: Filtro[K]) => setFiltro((f) => ({ ...f, [k]: v }));
  const sel = 'bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200';
  const chip = (on: boolean) => `px-2 py-1 rounded text-xs ring-1 ${on ? 'ring-emerald-500 text-emerald-300' : 'ring-slate-700 text-slate-400'}`;
  const alterado = filtro.departamento || filtro.motivo || filtro.sinal !== 'todos' || filtro.q;
  const SINAIS: Array<[string, string]> = [['todos', 'Todos'], ['risco', 'Risco'], ['cancelar', 'Falou em cancelar'], ['irritado', 'Irritado'],
    ['reincidente', 'Reincidente'], ['abandonado', 'Abandonado'], ['tecnico', 'Problema técnico']];
  return (
    <Card titulo="Atendimentos do dia">
      <div className="flex items-center gap-2 flex-wrap">
        {SINAIS.map(([v, l]) => <button key={v} className={chip(filtro.sinal === v)} onClick={() => set('sinal', v)}>{l}</button>)}
        <span className="mx-1 h-4 w-px bg-slate-700" />
        <select className={sel} value={filtro.departamento} onChange={(e) => set('departamento', e.target.value)}>
          <option value="">Departamento: todos</option>
          {q.data?.departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className={`${sel} max-w-56`} value={filtro.motivo} onChange={(e) => set('motivo', e.target.value)}>
          <option value="">Motivo: todos</option>
          {q.data?.motivos.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <input className={`${sel} w-56`} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="protocolo, cliente, id IXC, OLT, resumo" />
        {alterado && <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => { setFiltro({ departamento: '', motivo: '', sinal: 'todos', q: '' }); setTexto(''); }}>limpar</button>}
        <span className="ml-auto text-xs text-slate-500">{q.data ? `${q.data.atendimentos.length} atendimento(s)` : ''}{q.isFetching ? ' · atualizando…' : ''}</span>
      </div>
      <TabelaAtendimentos linhas={q.data?.atendimentos} carregando={q.isLoading} erro={q.isError} />
    </Card>
  );
}

function TabelaAtendimentos({ linhas, carregando, erro, comData }: { linhas?: Atendimento[]; carregando?: boolean; erro?: boolean; comData?: boolean }) {
  const [aberto, setAberto] = useState<string | null>(null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-slate-400 bg-slate-800/60">
          <tr>
            <th className="text-left px-2 py-1.5">Protocolo</th>
            <th className="text-left px-2 py-1.5">{comData ? 'Encerrado' : 'Aberto → encerrado'}</th>
            <th className="text-left px-2 py-1.5">Cliente</th>
            <th className="text-left px-2 py-1.5">Departamento / atendente</th>
            <th className="text-left px-2 py-1.5">Motivo</th>
            <th className="text-right px-2 py-1.5">1ª resp.</th>
            <th className="text-right px-2 py-1.5">Resolução</th>
            <th className="text-left px-2 py-1.5">Sinais</th>
            <th className="text-right px-2 py-1.5">Custo</th>
          </tr>
        </thead>
        <tbody>
          {carregando && <tr><td colSpan={9} className="p-4 text-center text-slate-500">carregando…</td></tr>}
          {erro && <tr><td colSpan={9} className="p-4 text-center text-rose-400">erro ao carregar</td></tr>}
          {linhas?.map((a) => (
            <FragmentoLinha key={a.id} a={a} aberto={aberto === a.id} alternar={() => setAberto(aberto === a.id ? null : a.id)} comData={comData} />
          ))}
          {linhas && linhas.length === 0 && <tr><td colSpan={9} className="p-4 text-center text-slate-500">nenhum atendimento</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function FragmentoLinha({ a, aberto, alternar, comData }: { a: Atendimento; aberto: boolean; alternar: () => void; comData?: boolean }) {
  return (
    <>
      <tr className="border-t border-slate-800 hover:bg-slate-800/40 cursor-pointer align-top" onClick={alternar}>
        <td className="px-2 py-1.5 font-mono text-cyan-400 whitespace-nowrap">{a.protocolo ?? '—'}</td>
        <td className="px-2 py-1.5 font-mono text-slate-400 whitespace-nowrap">
          {comData ? dt(a.encerradoEm) : `${hora(a.abertoEm)} → ${hora(a.encerradoEm)}`}
        </td>
        <td className="px-2 py-1.5">
          {a.idClienteIxc ? (
            <button className="text-left hover:underline" onClick={(e) => { e.stopPropagation(); go(`cliente/${a.idClienteIxc}`); }}>
              <span className="font-mono text-slate-500">{a.idClienteIxc}</span> <span className="text-slate-200">{a.nomeCliente ?? ''}</span>
            </button>
          ) : <span className="text-slate-500">{a.nomeCliente ?? 'sem cadastro'}</span>}
        </td>
        <td className="px-2 py-1.5">
          <div className="text-slate-300">{a.departamento ?? '—'}</div>
          <div className="text-slate-500">{a.tipoAtendente === 'bot' ? 'bot' : a.atendente ?? '—'}</div>
        </td>
        <td className="px-2 py-1.5 text-slate-300 max-w-56 truncate" title={a.motivo ?? ''}>{a.motivo ?? (a.analisadoEm ? '—' : <span className="text-slate-500">não analisado</span>)}</td>
        <td className="px-2 py-1.5 text-right"><Sla s={a.sla1} tipo="primeira" /></td>
        <td className="px-2 py-1.5 text-right"><Sla s={a.sla2} tipo="resolucao" /></td>
        <td className="px-2 py-1.5 space-x-1 whitespace-nowrap">
          <Flag on={a.falaCancelar} txt="cancelar" cor="ring-rose-500 text-rose-300" />
          <Flag on={a.risco} txt="risco" cor="ring-rose-700 text-rose-300" />
          <Flag on={a.irritado} txt="irritado" cor="ring-amber-600 text-amber-300" />
          <Flag on={a.abandonado} txt="abandonado" cor="ring-slate-500 text-slate-300" />
          <Flag on={a.problemaTecnico} txt="técnico" cor="ring-cyan-600 text-cyan-300" />
        </td>
        <td className="px-2 py-1.5 text-right font-mono text-slate-400 whitespace-nowrap">{usd4(a.custoUsd)}</td>
      </tr>
      {aberto && (
        <tr className="bg-slate-950/40">
          <td colSpan={9} className="px-3 py-2 text-xs space-y-1">
            <div className="text-slate-200 max-w-4xl">{a.resumo ?? 'sem resumo'}</div>
            <div className="text-slate-500">
              canal {a.canal ?? '—'} · {a.qtdMensagens} mensagem(ns) · analisado por {a.modelo ?? '—'} em {dt(a.analisadoEm)} ({usd4(a.custoUsd)}) · OLT/região {a.olt ?? '—'} · aberto {dt(a.abertoEm)} · encerrado {dt(a.encerradoEm)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------- histórico do cliente

function Cliente({ id }: { id: number }) {
  const q = useQuery<{ idClienteIxc: number; nome: string | null; atendimentos: Atendimento[] }>({
    queryKey: ['atd-agente', 'cliente', id],
    queryFn: () => api.get(`/api/atendimento-agente/clientes/${id}`)
  });
  const at = q.data?.atendimentos ?? [];
  return (
    <div className="p-6 space-y-4">
      <div>
        <button className="text-xs text-slate-400 hover:text-slate-200" onClick={() => window.history.back()}>← voltar</button>
        <h2 className="text-xl font-semibold">
          <span className="font-mono text-slate-400">{id}</span> {q.data?.nome ?? ''}
          <span className="ml-2 text-sm text-slate-500">histórico nos dias analisados</span>
        </h2>
      </div>
      {q.isError && <div className="text-rose-400">cliente sem atendimentos analisados</div>}
      {q.data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi l="Atendimentos" v={at.length} />
          <Kpi l="Dias com atendimento" v={new Set(at.map((a) => a.data)).size} />
          <Kpi l="Falou em cancelar" v={at.filter((a) => a.falaCancelar).length} cor={at.some((a) => a.falaCancelar) ? 'text-rose-400' : undefined} />
          <Kpi l="Problema técnico" v={at.filter((a) => a.problemaTecnico).length} />
        </div>
      )}
      <Card titulo="Atendimentos">
        <TabelaAtendimentos linhas={q.data?.atendimentos} carregando={q.isLoading} comData />
      </Card>
    </div>
  );
}
